import React, { useEffect, useRef } from 'react';

import { Slider } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import styled from 'styled-components';

import Checkbox from 'components/lib/Checkbox';
import InputNumber from 'components/lib/InputNumber';
import { MarketRateComparisonUpdateSettings } from 'components/Settings/types';
import { formatPercent } from 'utils/helpers/numbers';

const Container = styled.div`
  margin-top: 30px;
  display: flex;
  flex-flow: row;
  justify-content: space-between;
`;

const VerticalContainer = styled.div`
  display: flex;
  flex-flow: column;
  width: 48%;
`;

const PreferenceCheckbox = styled(Checkbox)`
  font-size: 14px;
  line-height: 1;
  align-items: flex-start;

  .ant-checkbox {
    margin-top: -24px;
  }
`;

const LabelEmphasis = styled.span`
  font-family: var(--font-bold);
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  column-gap: 7px;
  margin-top: 16px;
  margin-bottom: 10px;
  padding-left: 28px;
`;

const SliderValueLabel = styled.span`
  font-size: 14px;
  color: var(--text-primary);
  line-height: 1;
`;

const StyledSlider = styled(Slider)`
  flex: 1;
  margin: 0;
  padding: 10px 0;

  .ant-slider-rail,
  .ant-slider-track {
    height: 3px;
  }

  .ant-slider-rail {
    background-color: var(--background-gray-soft);
  }

  .ant-slider-track {
    background-color: var(--button-blue);
  }

  .ant-slider-handle {
    width: 16px;
    height: 16px;
    margin-top: -6px;
    border: 2px solid var(--white);
    border-radius: 50%;
    background-color: var(--button-blue);
    box-shadow: none;
    inset: unset;
  }

  .ant-slider-handle::before,
  .ant-slider-handle::after {
    display: none;
  }
`;

const InputRow = styled.div`
  padding-left: 55px;
`;

const PreferenceInputNumber = styled(InputNumber)`
  width: 55px;
  background-color: var(--white) !important;

  .ant-input-number-input {
    background-color: transparent;
    padding-right: 8px;
  }
`;

type Props = {
  notificationEnabled: boolean;
  initialOverMarketRateEnabled?: boolean;
  initialUnderMarketRateEnabled?: boolean;
  initialOverMarketRate?: number | null;
  initialUnderMarketRate?: number | null;
  onChange: (settings: MarketRateComparisonUpdateSettings) => void;
};

const DEFAULT_MARKET_RATE_THRESHOLD = 10;

const MarketRateComparisonPreferences = ({
  notificationEnabled,
  initialOverMarketRateEnabled,
  initialUnderMarketRateEnabled,
  initialOverMarketRate,
  initialUnderMarketRate,
  onChange,
}: Props) => {
  const hasOverValue = initialOverMarketRate !== null && initialOverMarketRate !== undefined;
  const hasUnderValue = initialUnderMarketRate !== null && initialUnderMarketRate !== undefined;
  const overMarketRateEnabled = Boolean(initialOverMarketRateEnabled);
  const underMarketRateEnabled = Boolean(initialUnderMarketRateEnabled);
  const overMarketRate = hasOverValue ? Number(initialOverMarketRate) : DEFAULT_MARKET_RATE_THRESHOLD;
  const underMarketRate = hasUnderValue ? Number(initialUnderMarketRate) : DEFAULT_MARKET_RATE_THRESHOLD;
  const prevNotificationEnabledRef = useRef<boolean>(notificationEnabled);

  const emitChange = (next: {
    overMarketRateEnabled?: boolean;
    underMarketRateEnabled?: boolean;
    overMarketRate?: number;
    underMarketRate?: number;
  }) => {
    onChange({
      enabled: true,
      overMarketRate: {
        enabled: next.overMarketRateEnabled ?? overMarketRateEnabled,
        value: next.overMarketRate ?? overMarketRate,
      },
      underMarketRate: {
        enabled: next.underMarketRateEnabled ?? underMarketRateEnabled,
        value: next.underMarketRate ?? underMarketRate,
      },
    });
  };

  useEffect(() => {
    const wasNotificationEnabled = prevNotificationEnabledRef.current;

    if (!wasNotificationEnabled && notificationEnabled && !overMarketRateEnabled && !underMarketRateEnabled) {
      emitChange({
        overMarketRateEnabled: true,
        underMarketRateEnabled: true,
      });
    }

    prevNotificationEnabledRef.current = notificationEnabled;
  }, [notificationEnabled, overMarketRateEnabled, underMarketRateEnabled]);

  const onOverMarketRateEnabledChange = (event: CheckboxChangeEvent) => {
    emitChange({ overMarketRateEnabled: event.target.checked });
  };

  const onUnderMarketRateEnabledChange = (event: CheckboxChangeEvent) => {
    emitChange({ underMarketRateEnabled: event.target.checked });
  };

  const onOverMarketRateChange = (value: number | null) => {
    if (value === null || isNaN(value)) {
      return;
    }
    emitChange({ overMarketRate: value });
  };
  const onUnderMarketRateChange = (value: number | null) => {
    if (value === null || isNaN(value)) {
      return;
    }
    emitChange({ underMarketRate: value });
  };

  if (!notificationEnabled) {
    return null;
  }

  return (
    <Container>
      <VerticalContainer>
        <PreferenceCheckbox className="green" checked={overMarketRateEnabled} onChange={onOverMarketRateEnabledChange}>
          When my own rate is <LabelEmphasis>over</LabelEmphasis> the competitors' by:
        </PreferenceCheckbox>
        <SliderRow>
          <SliderValueLabel>0%</SliderValueLabel>
          <StyledSlider
            min={0}
            max={100}
            value={overMarketRate}
            onChange={onOverMarketRateChange}
            disabled={!overMarketRateEnabled}
          />
          <SliderValueLabel>100%</SliderValueLabel>
        </SliderRow>
        <InputRow>
          <PreferenceInputNumber
            min={0}
            max={100}
            step={1}
            controls={false}
            value={overMarketRate}
            onChange={onOverMarketRateChange}
            disabled={!overMarketRateEnabled}
            formatter={formatPercent}
          />
        </InputRow>
      </VerticalContainer>
      <VerticalContainer>
        <PreferenceCheckbox
          className="green"
          checked={underMarketRateEnabled}
          onChange={onUnderMarketRateEnabledChange}
        >
          When my own rate is <LabelEmphasis>under</LabelEmphasis> the competitors' by:
        </PreferenceCheckbox>
        <SliderRow>
          <SliderValueLabel>0%</SliderValueLabel>
          <StyledSlider
            min={0}
            max={100}
            value={underMarketRate}
            onChange={onUnderMarketRateChange}
            disabled={!underMarketRateEnabled}
          />
          <SliderValueLabel>100%</SliderValueLabel>
        </SliderRow>
        <InputRow>
          <PreferenceInputNumber
            min={0}
            max={100}
            step={1}
            controls={false}
            value={underMarketRate}
            onChange={onUnderMarketRateChange}
            disabled={!underMarketRateEnabled}
            formatter={formatPercent}
          />
        </InputRow>
      </VerticalContainer>
    </Container>
  );
};

export default MarketRateComparisonPreferences;
